/* ============================================================================
   furbos-offer.js
   Owner of every price printed inside a quantity-breaks tier card.

   WHY THIS EXISTS
   ---------------
   The Shrine bundle (js.shrinetheme.com/js/v2/main.js) recomputes tier prices as

       total = base * qty * (1 - p)

   in a single multiplication.  Shopify does not charge that.  Measured on the
   live cart 2026-08-13, an automatic percentage discount is rounded to the cent
   PER UNIT and only then multiplied by the line quantity:

       AU refill qty3 @ 33.36%  ->  floor(3499 * 0.3336) = 1167 per unit
                                    3 * 1167 = 3501 discount
                                    10497 - 3501 = 6996 = A$69.96   (charged)
       theme model              ->  3499 * 3 * 0.6664 = 6998        (displayed)

   Two cents of drift, in the buyer's favour on the card and against them at
   checkout.  snippets/quantity-breaks.liquid now renders the per-unit model on
   first paint, and this file keeps it correct every time the refill toggle or
   the tier selection changes.  The tier spans were renamed from
   .variant-price-update to .furbos-price-update so the theme bundle cannot
   overwrite them with the old model.

   Everything is derived from the variant JSON the theme already prints inside
   <quantity-breaks> (price, compare_at_price, selling_plan_allocations), which
   Shopify renders in the buyer's market currency.  No price is typed anywhere
   in this file, so AU, US and any future market all follow automatically.
   ============================================================================ */
(function () {
  'use strict';

  var SEL = 'quantity-breaks';

  /* ---- per-tier refill plans, one set per market -------------------------
     2026-08-17. The bundle discount used to be a CART discount, which Shopify
     does not copy into the subscription contract. First order billed A$89.97,
     every renewal billed A$134.97, and checkout printed both. Two of the first
     five subscribers cancelled within 20 minutes of seeing it. The quantity
     discount now lives inside the Seal selling plan, so it recurs.

     2026-08-25. Seal stores ONE percentage per selling plan, and Shopify reads
     that percentage off whatever price the buyer's market shows. Australia
     sells off A$53.83 and the United States off $49.99, so a single set of
     percentages cannot hold both ladders at once. There are now two sets and
     the buyer's market picks one:

        AU   base A$53.83   35    / 47.37 / 61.01  ->  34.99 /  84.99 / 104.95
        US   base  $49.99   20    / 46.67 / 52.01  ->  39.99 /  79.98 / 119.95
        GB   base  L36.99   18.92 / 45.96 / 51.37  ->  29.99 /  59.97 /  89.95
        CA   base C$69.99   20    / 47.15 / 52.86  ->  55.99 / 110.97 / 164.95
        EU   base  E42.99   20.94 / 46.52 / 51.17  ->  33.99 /  68.97 / 104.95

     THE REASON EUROPE EXISTS AS ITS OWN MARKET. Shopify does not offer selling
     plans at all in a market that uses AUTO CURRENCY CONVERSION. Measured
     2026-08-25: Germany and Ireland returned ZERO selling plan allocations and
     the PDP printed Seal's "This product has no active subscription plans
     created" while quietly falling back to one-time pricing. Naeem hit exactly
     that on his phone. Giving Europe a FIXED EUR price list took Germany from 0
     plans to 12 immediately. Any future market MUST get a fixed price list or
     it silently becomes a one-time-only store.

     GB and CA were added 2026-08-25 from the US ladder converted at the live
     rate (USD->GBP 0.7335, USD->CAD 1.3838) and then rounded to a tidy local
     number, which is why their percentages are not the US ones. Shopify's own
     Rest of World conversion had been quoting GBP 48.00 for a 49.99 product,
     so both markets now carry FIXED price lists rather than auto conversion.

     Both sets exist on the dog and the cat product, and planForQty() still
     checks the id against the variant's own selling_plan_allocations before it
     is used, so a wrong set can never silently turn a refill into a one-time
     purchase. Anything that is not Australia takes the US set, which is also
     what Rest of World converts its prices from. */
  var PLANS_AU = { 1: '11644862699', 3: '11649679595', 5: '11649712363' };
  var PLANS_US = { 1: '11663048939', 3: '11663081707', 5: '11663114475' };
  var PLANS_GB = { 1: '11663704299', 3: '11663737067', 5: '11663769835' };
  var PLANS_CA = { 1: '11663802603', 3: '11663835371', 5: '11663868139' };
  var PLANS_EU = { 1: '11663966443', 3: '11663999211', 5: '11664031979' };

  var EURO = ('AT BE HR CY EE FI FR DE GR IE IT LV LT LU MT NL PT SK SI ES').split(' ');
  var PLANS_BY_COUNTRY  = { AU: PLANS_AU, US: PLANS_US, GB: PLANS_GB, CA: PLANS_CA };
  EURO.forEach(function (c) { PLANS_BY_COUNTRY[c] = PLANS_EU; });
  var PLANS_BY_CURRENCY = { AUD: PLANS_AU, USD: PLANS_US, GBP: PLANS_GB, CAD: PLANS_CA, EUR: PLANS_EU };

  /* Resolved on every call rather than once at load. Shopify.country is written
     by the markets script, and a cached document can run this file before it
     lands. Currency is the fallback because that is what the price the plan is
     applied to is actually denominated in. */
  function planSet() {
    var S = window.Shopify || {};
    var country = String(S.country || '').toUpperCase();
    var currency = (S.currency && S.currency.active)
      ? String(S.currency.active).toUpperCase() : '';
    if (PLANS_BY_COUNTRY[country]) return PLANS_BY_COUNTRY[country];
    if (PLANS_BY_CURRENCY[currency]) return PLANS_BY_CURRENCY[currency];
    return PLANS_US;   /* Rest of World converts from the USD base */
  }

  /* ---- free shipping threshold, per market -------------------------------
     Read off the delivery profile 2026-08-25, in that market's own cents:

        AU   A$4.99 standard, FREE at A$70      -> 7000
        US    $4.99 standard, FREE at $79.98    -> 7998   (the 3-pack price)
        GB   L3.99 standard, FREE over USD 70   -> 5134   (World zone, converted)
        CA   C$3.99 standard, FREE over USD 70  -> 9687   (World zone, converted)

     GB and CA sit in the World shipping zone, whose free threshold is set in
     shop currency (USD 70), so their local figures here are conversions and
     will drift with the rate. Both sit comfortably between the 1-pack and the
     3-pack in their market, so all three tiers report correctly either way.

     The snippet's data-free-ship-min is the fallback for any market not named
     here. It used to say 12000 on its own, a threshold no live rate has had for
     some time, so the 3- and 5-pack were both being told they would pay for
     shipping that checkout gives them free. Shipping is the single thing that
     killed ten of ten abandoned checkouts here, so this line has to be right in
     BOTH directions: never promise free shipping the checkout will not give,
     and never hide free shipping the checkout will. */
  var FREE_SHIP_MIN = { AU: 7000, US: 7998, GB: 5134, CA: 9687, EUR_DEFAULT: 6001 };

  function freeShipMin(root) {
    var S = window.Shopify || {};
    var country = String(S.country || '').toUpperCase();
    if (FREE_SHIP_MIN[country] != null) return FREE_SHIP_MIN[country];
    /* World zone is free over USD 70, which is EUR 60.01 at the rate this was
       set. Sits between the 1-pack and the 3-pack, so all three tiers report
       correctly even as the rate drifts. */
    if (EURO.indexOf(country) !== -1) return FREE_SHIP_MIN.EUR_DEFAULT;
    return parseInt(root.dataset.freeShipMin || '0', 10) || 0;
  }

  /* ---- money -------------------------------------------------------------- */

  function group(v, dec) {
    var parts = v.toFixed(dec).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  function money(cents, format) {
    var n = Math.round(cents) / 100;
    var noTrailing = Math.round(cents) % 100 === 0;
    return String(format || '${{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/g, noTrailing ? group(n, 0) : group(n, 2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, group(n, 0))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g,
        group(n, 2).replace(/,/g, '\u0001').replace('.', ',').replace(/\u0001/g, '.'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        group(n, 0).replace(/,/g, '.'))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g,
        group(n, 2).replace(/,/g, "'"));
  }

  /* ---- state -------------------------------------------------------------- */

  function variantOf(qb) {
    var tag = qb.querySelector('script[type="application/json"]');
    if (!tag) return null;
    var list;
    try { list = JSON.parse(tag.textContent); } catch (e) { return null; }
    if (!list || !list.length) return null;
    var form = document.getElementById(qb.dataset.formId || '') ||
      document.querySelector('form[id^="product-form-' + (qb.dataset.section || '') + '"]');
    var idInput = form && form.querySelector('input[name="id"]');
    if (idInput) {
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].id) === String(idInput.value)) return list[i];
      }
    }
    return list[0];
  }

  /* The buyer is on a refill when a selling plan is going to be posted with the
     line. Read the form first, because that is literally what gets charged. */
  function activePlanId(qb) {
    var section = qb.dataset.section || '';
    var form = document.querySelector('form[id^="product-form-' + section + '"]') ||
      qb.closest('.shopify-section') && qb.closest('.shopify-section').querySelector('form[data-type="add-to-cart-form"]');
    var input = form && form.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
    if (input && input.value) return String(input.value);

    var widget = (qb.closest('.shopify-section') || document)
      .querySelector('shrine-subscription-widget');
    if (widget) {
      var box = widget.querySelector('.subscription__box[data-type="subscription"]');
      if (box) {
        var cb = box.querySelector('input[type="checkbox"], input[type="radio"]');
        var on = cb ? cb.checked : box.dataset.selected === 'true';
        return on ? String(widget.dataset.firstSellingPlanId || '') : '';
      }
    }
    return '';
  }

  function formOf(qb) {
    var section = qb.dataset.section || '';
    return document.querySelector('form[id^="product-form-' + section + '"]') ||
      (qb.closest('.shopify-section') &&
       qb.closest('.shopify-section').querySelector('form[data-type="add-to-cart-form"]'));
  }

  /* The subscription widget is rendered by Liquid with the FIRST selling plan
     group on the product as its default, which is whichever Seal rule happens to
     sort first. That is the Australian 1-bottle plan. On any page carrying the
     widget, a US buyer was therefore shown the AU discount applied to the US
     base price: 49.99 x 0.65 = 32.49 instead of 39.99. Measured on the
     advertorial pages 2026-08-25.

     planForQty() also falls back to this id whenever a tier lookup misses, so a
     stale default is not cosmetic, it is what gets posted to the cart. Re-point
     every widget at the current market's 1-bottle plan before anything reads it. */
  function fixWidgetDefault(root) {
    var want = planSet()[1];
    if (!want) return;
    var widgets = (root || document).querySelectorAll('shrine-subscription-widget');
    for (var i = 0; i < widgets.length; i++) {
      var w = widgets[i];
      if (!w.dataset.firstSellingPlanId) continue;
      if (String(w.dataset.firstSellingPlanId) !== String(want)) {
        w.dataset.firstSellingPlanId = want;
      }
    }
  }

  function widgetOf(qb) {
    return (qb.closest('.shopify-section') || document)
      .querySelector('shrine-subscription-widget');
  }

  /* Is the buyer on a refill right now? The subscription box is the buyer's
     intent; the form input is only where that intent gets written. */
  function refillOn(qb) {
    var widget = widgetOf(qb);
    if (widget) {
      var box = widget.querySelector('.subscription__box[data-type="subscription"]');
      if (box) {
        var cb = box.querySelector('input[type="checkbox"], input[type="radio"]');
        if (cb) return !!cb.checked;
        return box.dataset.selected === 'true';
      }
    }
    var form = formOf(qb);
    var input = form && form.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
    return !!(input && input.value);
  }

  function selectedQty(qb) {
    var labels = qb.querySelectorAll('label.quantity-break');
    for (var i = 0; i < labels.length; i++) {
      var radio = document.getElementById(labels[i].getAttribute('for'));
      if (radio && radio.checked) return parseInt(labels[i].dataset.quantity, 10) || 1;
    }
    return 1;
  }

  /* Only return a plan the variant actually carries, so a stale id cannot turn a
     refill into a one-time purchase without anyone noticing. */
  function planForQty(qb, variant, qty) {
    var want = planSet()[qty];
    var allocs = variant && variant.selling_plan_allocations;
    if (want && allocs) {
      for (var i = 0; i < allocs.length; i++) {
        if (String(allocs[i].selling_plan_id) === String(want)) return String(want);
      }
    }
    var widget = widgetOf(qb);
    return widget ? String(widget.dataset.firstSellingPlanId || '') : '';
  }

  /* Write the tier's plan onto the form. This is what actually gets charged, so
     it is re-applied on every repaint and again the instant add-to-cart fires. */
  function syncPlanInput(qb) {
    var form = formOf(qb);
    var variant = variantOf(qb);
    if (!form || !variant) return;
    var want = refillOn(qb) ? planForQty(qb, variant, selectedQty(qb)) : '';
    var input = form.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
    if (!input) {
      if (!want) return;
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      form.appendChild(input);
    }
    if (String(input.value || '') !== String(want)) input.value = want;
  }

  function unitPrice(variant, planId) {
    if (planId && variant.selling_plan_allocations) {
      for (var i = 0; i < variant.selling_plan_allocations.length; i++) {
        var a = variant.selling_plan_allocations[i];
        if (String(a.selling_plan_id) === String(planId)) {
          return a.per_delivery_price != null ? a.per_delivery_price : a.price;
        }
      }
    }
    return variant.price;
  }

  /* ---- render ------------------------------------------------------------- */

  function fill(node, tokens) {
    var tpl = node.getAttribute('data-text');
    if (tpl == null) return;
    var out = tpl;
    for (var k in tokens) {
      if (Object.prototype.hasOwnProperty.call(tokens, k)) {
        out = out.split('[' + k + ']').join(tokens[k]);
      }
    }
    if (node.innerHTML !== out) node.innerHTML = out;
  }

  function renderBlock(qb) {
    var variant = variantOf(qb);
    if (!variant) return;
    /* The Liquid first paint puts the ISO code on the price and the per-bottle
       price only, and leaves it off the struck-through anchor and the saving.
       Mirror that exactly or the block flickers between two spellings. */
    var format = qb.dataset.moneyFormat || '${{amount}}';
    var plain = format.replace(/\s*[A-Z]{3}\s*$/, '');
    format = plain;   /* never render the ISO code anywhere */
    syncPlanInput(qb);
    var on = refillOn(qb);
    var compareAt = variant.compare_at_price || 0;

    var labels = qb.querySelectorAll('label.quantity-break');
    var selectedTotal = null;
    var selected = null;
    var selectedIdx = 0;

    for (var i = 0; i < labels.length; i++) {
      var label = labels[i];
      var qty = parseInt(label.dataset.quantity, 10) || 1;

      /* On a refill the pack discount is already inside the plan price, so the
         tier percentage must not be applied a second time. On a one-time
         purchase the automatic cart discount is what the buyer gets, so the
         tier percentage is the right model. */
      var unit = on ? unitPrice(variant, planForQty(qb, variant, qty)) : variant.price;
      var pct = on ? 0 : (parseFloat(label.dataset.percentageOff || '0') || 0);

      /* Shopify's own arithmetic, measured on the live cart 2026-08-13:
         truncate the discount to the cent per unit, then multiply by quantity.
         floor, not round: US refill qty3 at 33.36% charged 833c per unit, and
         round(2499 * 0.3336) is 834. */
      var priceEach = unit - Math.floor((unit * pct) / 100 + 1e-9);
      /* Ariel: the tier figures are masking prices, they only have to sit near the real
         per-unit price and look tidy. Snap UP to the next .99 so every market lands pretty
         with no hardcoded numbers, and so the buyer is always charged a little LESS than
         the figure shown, never more. */
      priceEach = (priceEach % 100 === 99) ? priceEach : (Math.ceil(priceEach / 100) * 100 - 1);
      var total = priceEach * qty;

      var compareEach = unit;
      if (label.dataset.compareMode === 'compare_price' && compareAt > unit) compareEach = compareAt;
      var compareTotal = compareEach * qty;

      var pctSaved = compareEach > 0
        ? Math.floor(((compareEach - priceEach) * 100) / compareEach) : 0;
      var saved = compareTotal - total;

      var tokens = {
        quantity: qty,
        price: money(total, format),
        price_each: money(priceEach, format),
        compare_price: money(compareTotal, plain),
        compare_price_each: money(compareEach, plain),
        amount_saved: money(saved, plain),
        amount_saved_rounded: money(Math.round(saved / 100) * 100, plain),
        percentage_saved: pctSaved + '%'
      };

      var nodes = label.querySelectorAll('.furbos-price-update[data-text]');
      for (var j = 0; j < nodes.length; j++) fill(nodes[j], tokens);

      var cmp = label.querySelector('.quantity-break__compare-price');
      if (cmp) cmp.classList.toggle('hidden', compareTotal <= total);

      label.dataset.furbosTotal = total;
      var radio = document.getElementById(label.getAttribute('for'));
      if (radio && radio.checked) {
        selectedTotal = total;
        selected = { qty: qty, each: priceEach, total: total };
        selectedIdx = i;
      }
    }

    if (selectedTotal != null) paintAtc(qb, selectedTotal, format);
    paintAtcChrome(qb);
    paintGifts(qb, selectedIdx + 1);
    paintRefill(qb, on, selected, format);
  }

  /* ---- refill terms ------------------------------------------------------- */

  /* The two-box subscribe/one-time selector is hidden by furbos-offer.css. This
     block is what the buyer actually reads, and the hidden radios are still the
     only thing that decides what gets posted, so every state change goes through
     a real click on a real input rather than a flag kept on the side. */
  function subBox(qb, type) {
    var widget = widgetOf(qb);
    return widget && widget.querySelector('.subscription__box[data-type="' + type + '"]');
  }

  /* The buy-buttons block renders its label from Liquid we do not own, so the
     guarantee and the arrow are appended once and then left alone. Idempotent:
     it checks for its own node before touching anything. */
  function paintAtcChrome(qb) {
    var scope = qb.closest('.shopify-section') || document;
    var labels = scope.querySelectorAll('.main-atc__label');
    for (var i = 0; i < labels.length; i++) {
      var label = labels[i];
      if (label.querySelector('.furbos-atc__row')) continue;
      var text = label.querySelector('.main-atc__label__text');
      if (!text) continue;

      /* line 1: the call to action and the arrow, on one row */
      var row = document.createElement('span');
      row.className = 'furbos-atc__row';
      label.insertBefore(row, text);
      row.appendChild(text);
      var arrow = document.createElement('span');
      arrow.className = 'furbos-atc__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '\u2192';
      row.appendChild(arrow);

      /* The guarantee used to sit on a second line here. Removed 2026-08-17 at
         Ariel's call: the button is a single instruction again. The guarantee
         still appears in the accordion and on the checkout banner. */
    }
  }

  /* Tier 1 unlocks gift 1, tier 2 unlocks gifts 1-2, tier 3 unlocks all three.
     Driven off the tier's POSITION, not its quantity, so changing 1/3/5 to any
     other pack sizes needs no change here. */
  function paintGifts(qb, unlockedCount) {
    var scope = qb.closest('.shopify-section') || document;
    var box = scope.querySelector('[data-furbos-gifts]');
    if (!box) return;
    box.dataset.unlocked = unlockedCount;
    var items = box.querySelectorAll('.furbos-gifts__item');
    for (var i = 0; i < items.length; i++) {
      var locked = i >= unlockedCount;
      items[i].classList.toggle('is-locked', locked);
      items[i].setAttribute('aria-disabled', locked ? 'true' : 'false');
    }
  }

  function paintRefill(qb, on, selected, format) {
    var scope = qb.closest('.shopify-section') || document;
    var root = scope.querySelector('[data-furbos-refill]');
    if (!root || !selected) return;

    root.classList.toggle('furbos-refill--onetime', !on);

    var cadence = root.querySelector('[data-furbos-refill-cadence]');
    if (cadence) {
      cadence.textContent = on
        ? (selected.qty === 1
            ? 'One bottle, refilled every month at ' + money(selected.each, format)
            : selected.qty + ' bottles, refilled every month at ' + money(selected.each, format) + ' each')
        : (selected.qty === 1 ? 'One bottle, delivered once' : selected.qty + ' bottles, delivered once');
    }

    /* Say the shipping outcome on the page. Ten of ten abandoned checkouts died
       at the moment shipping first appeared, so it is named here instead. */
    var ship = root.querySelector('[data-furbos-refill-ship]');
    if (ship) {
      var min = freeShipMin(root);
      ship.textContent = (min && selected.total >= min)
        ? 'Free shipping included'
        : (root.dataset.shipFeeNote || 'Standard shipping added at checkout');
    }

    var flex = root.querySelector('[data-furbos-refill-flex]');
    if (flex) {
      flex.textContent = on
        ? 'Skip or change your delivery date anytime'
        : 'One delivery, no repeat charge';
    }

    var label = root.querySelector('[data-furbos-switch-label]');
    if (label) {
      label.textContent = on ? 'Buy once, no savings' : 'Back to monthly refill';
    }
    var btn = root.querySelector('[data-furbos-buy-once]');
    if (btn) btn.setAttribute('aria-pressed', on ? 'false' : 'true');

    var note = root.querySelector('[data-furbos-refill-note]');
    if (note) {
      note.textContent = on
        ? 'Refills keep the barrier strong so the itching does not creep back.'
        : 'Without refills the barrier can weaken and the itching creep back.';
    }
  }

  /* The add to cart button and the sticky bar must show the pack total, not the
     unit price, or the button contradicts the card the buyer just clicked. */
  function paintAtc(qb, total, format) {
    var scope = qb.closest('.shopify-section') || document;
    var text = money(total, format);

    /* Inside the button. */
    var spans = scope.querySelectorAll('[class*="main-atc-price-span-"]');
    for (var i = 0; i < spans.length; i++) {
      if (spans[i].textContent.trim() !== text) spans[i].textContent = text;
    }

    /* The sticky bar renders a full price block, and the theme fills its
       compare slot with compare_at x qty. On a 5 pack that is a A$419.95
       anchor on a dog supplement, which nobody believes, so the bar carries the
       pack total alone and furbos-offer.css hides the anchor and its badge. */
    var bars = scope.querySelectorAll('.sticky-atc__price .price');
    for (var b = 0; b < bars.length; b++) {
      var items = bars[b].querySelectorAll('.price-item--regular, .price-item--sale');
      for (var k = 0; k < items.length; k++) {
        if (items[k].closest('.price__compare-price')) continue;
        if (items[k].textContent.trim() !== text) items[k].textContent = text;
      }
    }
  }

  function renderAll() {
    try { fixWidgetDefault(document); } catch (e) { /* never break the page over a price */ }
    var blocks = document.querySelectorAll(SEL);
    for (var i = 0; i < blocks.length; i++) {
      try { renderBlock(blocks[i]); } catch (e) { /* never break the page over a price */ }
    }
  }

  /* ---- wiring ------------------------------------------------------------- */

  /* The theme bundle repaints on the same interactions, so repaint after it on a
     short tail rather than fighting it with a MutationObserver. */
  function schedule() {
    renderAll();
    [0, 60, 200, 500, 900].forEach(function (t) { setTimeout(renderAll, t); });
  }

  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('quantity-breaks, shrine-subscription-widget, .subscription__box')) schedule();
  }, true);

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('quantity-breaks, shrine-subscription-widget, .subscription__box')) schedule();
  }, true);

  /* Capture phase: run before the theme's own add-to-cart handler, so the line
     is posted with the tier's plan even when the theme adds to cart over fetch
     and never fires a submit event. */
  function forceSync(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var hit = t.closest('[name="add"], .product-form__submit, form[action*="/cart/add"]');
    if (!hit) return;
    var scope = hit.closest('.shopify-section') || document;
    var blocks = scope.querySelectorAll(SEL);
    for (var i = 0; i < blocks.length; i++) {
      try { syncPlanInput(blocks[i]); } catch (err) { /* never block a sale */ }
    }
  }
  document.addEventListener('click', forceSync, true);
  document.addEventListener('submit', forceSync, true);

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-furbos-buy-once]');
    if (!btn) return;
    e.preventDefault();
    var scope = btn.closest('.shopify-section') || document;
    var qb = scope.querySelector(SEL);
    if (!qb) return;
    /* Two widget shapes exist. The radio shape renders a second box for the
       one-time option. The checkbox shape, which is what this store runs,
       renders ONE box carrying no input at all and keeps its state in
       data-selected, so the way to buy once is to deselect the only box.
       Measured on the live widget 2026-08-17. */
    var goingOneTime = refillOn(qb);
    var target = goingOneTime ? subBox(qb, 'onetime') : subBox(qb, 'subscription');
    if (!target) target = subBox(qb, 'subscription');
    if (!target) return;
    var input = target.querySelector('input[type="checkbox"], input[type="radio"]');
    if (input) { input.click(); } else { target.click(); }
    schedule();
  }, false);

  document.addEventListener('shopify:section:load', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  window.addEventListener('load', schedule);

  window.furbosOfferRender = renderAll;
})();
